import { Injectable } from '@angular/core';
import { AttributesSectionRefreshEvent } from 'src/app/models/events/attributes-section-refresh-event';
import { CommentViewerRefreshEvent } from 'src/app/models/events/comment-viewer-refresh-event';
import { FolderListExportEvent } from 'src/app/models/events/folder-list-export-event';
import { FolderListRefreshEvent } from 'src/app/models/events/folder-list-refresh-event';
import { FolderMenuRefreshEvent, MenuType } from 'src/app/models/events/folder-menu-refresh-event';
import { AttributesSectionEventsService } from 'src/app/services/events/attributes-section-events.service';
import { BoxCounterEventsService } from 'src/app/services/events/box-counter-events.service';
import { DocumentsSectionEventsService } from 'src/app/services/events/documents-section-events.service';
import { FavoritesEventsService } from 'src/app/services/events/favorites-events.service';
import { FolderListEventsService } from 'src/app/services/events/folder-list-events.service';
import { FolderMenuRefreshEventsService } from 'src/app/services/events/folder-menu-refresh-events.service';
import { FolderTabEventsService } from 'src/app/services/events/folder-tab-events.service';
import { HistorySectionEventsService } from 'src/app/services/events/history-section-events.service';
import { ViewCounterEventsService } from 'src/app/services/events/view-counter-events.service';
import { WorkflowSectionEventsService } from 'src/app/services/events/workflow-section-events.service';
import { FolderHistoriesService } from 'src/app/services/http/folder-histories.service';
import { FoldersService } from 'src/app/services/http/folders.service';
import { BoxCounterUpdatedEvent } from '../../models/events/box-counter-updated-event';
import { FavoritesRefreshEvent } from '../../models/events/favorites-refresh-event';
import { FolderCloseTabRequestEvent } from '../../models/events/folder-close-tab-request-event';
import { FolderDocumentsRefreshEvent } from '../../models/events/folder-documents-refresh-event';
import { FolderListDeletedEvent } from '../../models/events/folder-list-deleted-event';
import { FolderListUpdatedEvent } from '../../models/events/folder-list-updated-event';
import { FolderRenameTabRequestEvent } from '../../models/events/folder-rename-tab-request-event';
import { HistorySectionRefreshEvent } from '../../models/events/history-section-refresh-event';
import { ViewCounterUpdatedEvent } from '../../models/events/view-counter-updated-event';
import { WorkflowSectionRefreshEvent } from '../../models/events/workflow-section-refresh-event';
import { Folder } from '../../models/server/folder';
import { FolderDocument } from '../../models/server/folder-document';
import { FolderWorkflowStepsSnapshot } from '../../models/server/folder-workflow-steps-snapshot';
import { FolderCommentsService } from '../http/folder-comments.service';
import { OpenedFoldersStateService } from '../state/opened-folders-state.service';
import { ViewerEventsService } from './viewer-events.service';
import { FollowedFoldersEventsService } from './followed-folders-events.service';

@Injectable({
    providedIn: 'root',
})
export class EventsManagerService {
    public constructor(
        private attributesSectionEventsService: AttributesSectionEventsService,
        private boxCounterEventsService: BoxCounterEventsService,
        private documentsSectionEventsService: DocumentsSectionEventsService,
        private favoritesEventsService: FavoritesEventsService,
        private folderListEventsService: FolderListEventsService,
        private followedFoldersEventsService: FollowedFoldersEventsService,
        private folderMenuRefreshEventsService: FolderMenuRefreshEventsService,
        private folderTabEventsService: FolderTabEventsService,
        private historySectionEventsService: HistorySectionEventsService,
        private viewCounterEventsService: ViewCounterEventsService,
        private workflowSectionEventsService: WorkflowSectionEventsService,
        private folderHistoriesService: FolderHistoriesService,
        private foldersService: FoldersService,
        private openedFoldersStateService: OpenedFoldersStateService,
        private viewerEventsService: ViewerEventsService,
        private folderCommentsService: FolderCommentsService
    ) {}

    public emitHistorySectionUpdatedEvent(viewId: number, folderId: string) {
        // Emet un événement si le détail du dossier est ouvert
        if (this.openedFoldersStateService.openedFolders.hasKey(folderId)) {
            this.folderHistoriesService.list(viewId, folderId).subscribe((histories) => {
                this.historySectionEventsService.needRefresh.next(new HistorySectionRefreshEvent(folderId, histories));
            });
        }
    }

    public emitAttributesSectionUpdatedEvent(viewId: number, element: string | Folder) {
        if (typeof element === 'string') {
            // Emet un événement si le détail du dossier est ouvert
            if (this.openedFoldersStateService.openedFolders.hasKey(element)) {
                this.foldersService.get(viewId, element).subscribe((folder) => {
                    this.emitAttributesSectionUpdatedEvent(viewId, folder);
                });
            }
        } else {
            if (this.openedFoldersStateService.openedFolders.hasKey(element.id)) {
                this.attributesSectionEventsService.needRefresh.next(new AttributesSectionRefreshEvent(element));
            }
        }
    }

    public emitFavoriteSectionUpdatedEvent(viewId: number, element: string | Folder) {
        // Emet un événement si le détail du dossier est ouvert
        if (typeof element === 'string') {
            if (this.openedFoldersStateService.openedFolders.hasKey(element)) {
                this.foldersService.get(viewId, element).subscribe((folder) => {
                    this.emitFavoriteSectionUpdatedEvent(viewId, folder);
                });
            }
        } else {
            if (this.openedFoldersStateService.openedFolders.hasKey(element.id)) {
                this.favoritesEventsService.needRefresh.next(new FavoritesRefreshEvent(element));
            }
        }
    }

    public emitDocumentsUpdatedEvent(folderId: string, documents: FolderDocument[], selectedDocumentId: string = null, isDeleted: boolean = false) {
        this.emitDocumentsSectionUpdatedEvent(folderId, documents, selectedDocumentId, isDeleted);
        this.emitFolderDocumentsUpdatedEvent(folderId, documents, selectedDocumentId, isDeleted);
    }

    public emitDocumentsSectionUpdatedEvent(folderId: string, documents: FolderDocument[], selectedDocumentId: string = null, isDeleted: boolean = false) {
        // Emet un événement si le détail du dossier est ouvert
        if (this.openedFoldersStateService.openedFolders.hasKey(folderId)) {
            this.documentsSectionEventsService.needRefresh.next(new FolderDocumentsRefreshEvent(folderId, documents, selectedDocumentId, isDeleted));
        }
    }

    public emitFolderDocumentsUpdatedEvent(folderId: string, documents: FolderDocument[], selectedDocumentId: string = null, isDeleted: boolean = false) {
        this.folderListEventsService.folderDocumentsUpdated.next(new FolderDocumentsRefreshEvent(folderId, documents));
    }

    public emitBoxCounterUpdatedEvent(boxId: string, count: number) {
        this.boxCounterEventsService.counterUpdated.next(new BoxCounterUpdatedEvent(boxId, count));
    }

    public emitViewCounterUpdatedEvent(viewId: number, count: number) {
        this.viewCounterEventsService.counterUpdated.next(new ViewCounterUpdatedEvent(viewId, count));
    }

    public emitWorkflowSectionUpdatedeEvent(folderId: string, stepSnapshots: FolderWorkflowStepsSnapshot[]) {
        // Emet un événement si le détail du dossier est ouvert
        if (this.openedFoldersStateService.openedFolders.hasKey(folderId)) {
            this.workflowSectionEventsService.needRefresh.next(new WorkflowSectionRefreshEvent(folderId, stepSnapshots));
        }
    }

    public emitListFolderUpdatedEvent(viewId: number, element: string | Folder) {
        if (typeof element === 'string') {
            this.foldersService.get(viewId, element).subscribe((folder) => {
                // Initialise la proprité expand (qui n'est pas fournie)
                folder.expanded = false;
                this.emitListFolderUpdatedEvent(viewId, folder);
            });
        } else {
            this.folderListEventsService.foldersUpdated.next(new FolderListUpdatedEvent(element));
        }
    }

    public emitFollowedFoldersUpdatedEvent(viewId: number, element: string | Folder) {
        if (typeof element === 'string') {
            this.foldersService.get(viewId, element).subscribe((folder) => {
                // Initialise la proprité expand (qui n'est pas fournie)
                folder.expanded = false;
                this.emitFollowedFoldersUpdatedEvent(viewId, folder);
            });
        } else {
            this.followedFoldersEventsService.foldersUpdated.next(new FolderListUpdatedEvent(element));
        }
    }

    public emitListFolderDeletedEvent(folderId: string) {
        this.folderListEventsService.foldersDeleted.next(new FolderListDeletedEvent(folderId));
    }

    public emitFollowedFoldersDeletedEvent(folderId: string) {
        this.followedFoldersEventsService.foldersDeleted.next(new FolderListDeletedEvent(folderId));
    }

    public emitListRefreshEvent() {
        this.folderListEventsService.listNeedRefresh.next(new FolderListRefreshEvent());
    }

    public emitFollowedFoldersRefreshEvent() {
        this.followedFoldersEventsService.listNeedRefresh.next(new FolderListRefreshEvent());
    }

    public emitFolderTabCloseRequestEvent(folderId: string, isFolderRemoved: boolean) {
        // Emet un événement si le détail du dossier est ouvert
        if (this.openedFoldersStateService.openedFolders.hasKey(folderId)) {
            this.folderTabEventsService.closed.next(new FolderCloseTabRequestEvent(folderId, isFolderRemoved));
        }
    }

    public emitFolderRenamedEvent(folderId: string, newName: string) {
        // Emet un événement si le détail du dossier est ouvert
        if (this.openedFoldersStateService.openedFolders.hasKey(folderId)) {
            this.folderTabEventsService.renamed.next(new FolderRenameTabRequestEvent(folderId, newName));
        }
    }

    public emitFolderMenuRefreshEvent(folderId: string, menuType: MenuType, toggleValue: boolean) {
        // Emet un événement si le détail du dossier est ouvert
        if (this.openedFoldersStateService.openedFolders.hasKey(folderId)) {
            this.folderMenuRefreshEventsService.needRefresh.next(new FolderMenuRefreshEvent(folderId, menuType, toggleValue));
        }
    }

    public emitFolderListExportEvent() {
        this.folderListEventsService.exportRequest.next(new FolderListExportEvent());
    }

    public emitCommentChangeEvent(folderId: string, commentDocument: FolderDocument) {
        // Emet un événement si le détail du dossier est ouvert
        if (this.openedFoldersStateService.openedFolders.hasKey(folderId)) {
            this.viewerEventsService.commentChanged.next(new CommentViewerRefreshEvent(folderId, commentDocument));
        }

        // TODO : Emettre un evenement pour raffraichir le détail déplié d'un dossier depuis la liste des dossiers
    }

    public emitCommentChangeEvent2(viewId: number, folderId: string) {
        // Emet un événement si le détail du dossier est ouvert
        if (this.openedFoldersStateService.openedFolders.hasKey(folderId)) {
            this.folderCommentsService.getComment(viewId, folderId).subscribe((commentDocument) => {
                this.viewerEventsService.commentChanged.next(new CommentViewerRefreshEvent(folderId, commentDocument));
            });
        }
    }

    /**
     * Retourne une valeur indiquant si le dossier est ouvert par l'utilisateur courant.
     * @param folderId : Id du dossier
     * @returns : True si le dossier est ouvert par l'utilisateur courant.
     */
    public IsOpenedByCurrentUser(folderId: string) {
        return this.openedFoldersStateService.openedFolders.hasKey(folderId);
    }
}
